import { useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import { useTheme } from "@mui/material/styles";

import IconResolver from "../../../iconography/IconResolver";

const menuPlacements = {
  "bottom-end": {
    anchorOrigin: { horizontal: "right", vertical: "bottom" },
    transformOrigin: { horizontal: "right", vertical: "top" },
  },
  "bottom-start": {
    anchorOrigin: { horizontal: "left", vertical: "bottom" },
    transformOrigin: { horizontal: "left", vertical: "top" },
  },
  "top-end": {
    anchorOrigin: { horizontal: "right", vertical: "top" },
    transformOrigin: { horizontal: "left", vertical: "bottom" },
  },
  "top-start": {
    anchorOrigin: { horizontal: "left", vertical: "top" },
    transformOrigin: { horizontal: "right", vertical: "bottom" },
  },
};

function ActionIcon({ color = "currentColor", icon, size = 18 }) {
  if (!icon) return null;
  if (typeof icon === "string") {
    return <IconResolver color={color} name={icon} size={size} />;
  }

  return icon;
}

export default function ActionMenu({
  actions = [],
  ariaLabel = "Abrir menu de ações",
  icon,
  menuId,
  minWidth = 144,
  onClose,
  placement = "bottom-end",
  renderTrigger,
  triggerSx,
}) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const visibleActions = actions.filter((action) => !action.hidden);
  const resolvedPlacement = menuPlacements[placement] || menuPlacements["bottom-end"];

  if (!visibleActions.length) return null;

  const closeMenu = () => {
    setAnchorEl(null);
    onClose?.();
  };

  const buttonProps = {
    "aria-expanded": open ? "true" : undefined,
    "aria-haspopup": "menu",
    "aria-label": ariaLabel,
    onClick: (event) => setAnchorEl(event.currentTarget),
    type: "button",
  };

  return (
    <>
      {renderTrigger ? (
        renderTrigger({ buttonProps, open })
      ) : (
        <IconButton
          {...buttonProps}
          size="small"
          sx={{
            color: "text.secondary",
            height: 32,
            width: 32,
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
              color: theme.palette.text.primary,
            },
            ...triggerSx,
          }}
        >
          {icon || <MoreVertRoundedIcon fontSize="small" />}
        </IconButton>
      )}

      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        anchorOrigin={resolvedPlacement.anchorOrigin}
        transformOrigin={resolvedPlacement.transformOrigin}
        MenuListProps={{ "aria-label": ariaLabel }}
        PaperProps={{
          sx: {
            marginTop: placement.startsWith("top") ? 0 : undefined,
            minWidth,
          },
        }}
      >
        {visibleActions.map((action) => {
          const isDanger = action.tone === "danger" || action.color === "error";
          const iconPosition = action.iconPosition || "end";
          const iconColor = isDanger ? theme.palette.error.main : theme.palette.text.secondary;
          const startIcon = action.startIcon || (iconPosition === "start" ? action.icon : null);
          const endIcon = action.endIcon || (iconPosition === "end" ? action.icon : null);

          return (
            <MenuItem
              key={action.id || action.label}
              disabled={action.disabled}
              onClick={(event) => {
                action.onClick?.(event);
                if (action.closeOnSelect !== false) closeMenu();
              }}
              sx={{
                color: isDanger ? theme.palette.error.main : undefined,
                gap: 1,
                ...action.sx,
              }}
            >
              {startIcon ? (
                <Box
                  aria-hidden
                  sx={{
                    alignItems: "center",
                    color: iconColor,
                    display: "flex",
                    flexShrink: 0,
                  }}
                >
                  <ActionIcon color="currentColor" icon={startIcon} size={action.iconSize || 18} />
                </Box>
              ) : null}

              <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                {action.label}
              </Box>

              {endIcon ? (
                <Box
                  aria-hidden
                  sx={{
                    alignItems: "center",
                    color: iconColor,
                    display: "flex",
                    flexShrink: 0,
                    ml: 1,
                  }}
                >
                  <ActionIcon color="currentColor" icon={endIcon} size={action.iconSize || 18} />
                </Box>
              ) : null}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
